import { useId, useRef, useState, type ReactNode } from "react"
import { Meeting } from "@/schema"
import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import { badgeVariants, Button } from "@ui/shad"
import { cn } from "@ui/utils"
import { isPast } from "date-fns"
import { Loader2, MessageSquare, X } from "lucide-react"

import { isMeetingPromptFinished } from "@/lib/prompt-status"
import { CommentSection } from "@/components/comments"

import MeetingPastView from "./meeting-past-view"
import MeetingPrepView from "./meeting-prep-view"
import MeetingReviewView from "./meeting-review-view"

interface MeetingViewProps {
  isLoading: boolean
  meeting?: Meeting
  startRecording?: boolean
  initialSeekMs?: number
  initialTab?: "tasks" | "comments"
}

function MeetingPageWithComments({ meeting, children }: { meeting: Meeting; children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[calc(100vh-48px)]">
      <div className="w-[60%] min-w-0 border-r">{children}</div>
      <aside className="flex h-[calc(100vh-48px)] min-h-0 w-[40%] flex-col">
        <h2 className="shrink-0 border-b px-4 py-3 text-sm font-semibold">Comments</h2>
        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <CommentSection resourceType="Meeting" resourceId={meeting.id} />
        </div>
      </aside>
    </div>
  )
}

function MeetingPrepPageWithComments({
  meeting,
  startRecording,
  initialCommentsOpen = false,
}: {
  meeting: Meeting
  startRecording?: boolean
  initialCommentsOpen?: boolean
}) {
  const [isCommentsOpen, setIsCommentsOpen] = useState(initialCommentsOpen)
  const [commentCount, setCommentCount] = useState(meeting.comments_count ?? 0)
  const commentsPanelId = useId()
  const commentsButtonRef = useRef<HTMLButtonElement>(null)

  const handleCloseComments = () => {
    setIsCommentsOpen(false)
    commentsButtonRef.current?.focus()
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-48px)]">
      <div className="min-w-0 flex-1">
        <MeetingPrepView
          meeting={meeting}
          isLoading={false}
          startRecording={startRecording}
          commentsAction={
            <Button
              ref={commentsButtonRef}
              type="button"
              variant="ghost"
              size="sm"
              aria-expanded={isCommentsOpen}
              aria-controls={commentsPanelId}
              aria-label={isCommentsOpen ? "Hide comments" : "View comments"}
              onClick={() => setIsCommentsOpen((open) => !open)}
            >
              <MessageSquare />
              {isCommentsOpen ? "Hide comments" : "View comments"}
              {commentCount > 0 && (
                <span className={badgeVariants({ variant: "secondary", size: "xs" })}>{commentCount}</span>
              )}
            </Button>
          }
        />
      </div>
      <aside
        id={commentsPanelId}
        aria-label="Comments"
        aria-hidden={!isCommentsOpen}
        inert={!isCommentsOpen}
        className={cn(
          "flex h-[calc(100vh-48px)] min-h-0 shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-linear motion-reduce:transition-none",
          isCommentsOpen ? "w-88 max-w-full border-l" : "w-0"
        )}
      >
        <div className="flex h-11 shrink-0 items-center justify-between gap-2 border-b px-3">
          <h2 className="text-sm font-semibold whitespace-nowrap">Comments</h2>
          <Button type="button" variant="ghost" size="iconXs" aria-label="Close comments" onClick={handleCloseComments}>
            <X />
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          <CommentSection resourceType="Meeting" resourceId={meeting.id} onCommentCountChange={setCommentCount} />
        </div>
      </aside>
    </div>
  )
}

export function hasContent(meeting: Meeting): boolean {
  const hasAiSummary =
    meeting.ai_summary && (meeting.ai_summary.html || meeting.ai_summary.markdown || meeting.ai_summary.nodes)
  const hasSummary = Boolean(meeting.quick_summary || hasAiSummary)
  const hasFinishedSummaryRun =
    isMeetingPromptFinished(meeting, "Prompt::MeetingQuickSummary") ||
    isMeetingPromptFinished(meeting, "Prompt::MeetingNote")
  const hasTranscript =
    (meeting.transcript && meeting.transcript.length > 0) || meeting.user_uploaded_transcript || meeting.has_transcript
  const hasNotetakerCompleted =
    meeting.notetaker && "status" in meeting.notetaker && meeting.notetaker.status === "completed"
  const hasRecordings = meeting.has_recordings || meeting.is_processing_recording

  return hasSummary || hasFinishedSummaryRun || Boolean(hasTranscript || hasNotetakerCompleted || hasRecordings)
}

export function isMeetingPast(meeting: Meeting): boolean {
  if (meeting.has_recordings) return true
  const end = meeting.end_time ? new Date(meeting.end_time) : undefined
  const start = meeting.start_time ? new Date(meeting.start_time) : undefined
  return end ? isPast(end) : start ? isPast(start) : false
}

export default function MeetingView({
  isLoading,
  meeting,
  startRecording,
  initialSeekMs,
  initialTab,
}: MeetingViewProps) {
  const { isRecording, meetingId: recordingMeetingId, processingMeetingId } = useMeetingRecordingStore()
  const isRecordingThisMeeting = isRecording && recordingMeetingId === meeting?.id
  const isProcessingThisMeeting = processingMeetingId === meeting?.id

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <Loader2 className="text-muted-foreground size-8 animate-spin" />
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="flex h-[calc(100vh-48px)] items-center justify-center">
        <span className="text-destructive">Failed to load meeting</span>
      </div>
    )
  }

  // Keep the prep editor mounted while a local recording finishes processing.
  if (startRecording || isRecordingThisMeeting || isProcessingThisMeeting) {
    return (
      <MeetingPrepPageWithComments
        key={meeting.id}
        meeting={meeting}
        startRecording={startRecording}
        initialCommentsOpen={initialTab === "comments"}
      />
    )
  }

  // Has content (transcript, summary, tasks, recordings) → Review view
  if (hasContent(meeting)) {
    return (
      <MeetingReviewView
        meeting={meeting}
        isLoading={isLoading}
        initialSeekMs={initialSeekMs}
        initialTab={initialTab}
      />
    )
  }

  // Past meeting without content → Past view (upload prompt)
  if (isMeetingPast(meeting)) {
    return (
      <MeetingPageWithComments meeting={meeting}>
        <MeetingPastView meeting={meeting} isLoading={isLoading} />
      </MeetingPageWithComments>
    )
  }

  // Upcoming/active meeting → Prep view
  return (
    <MeetingPrepPageWithComments
      key={meeting.id}
      meeting={meeting}
      startRecording={startRecording}
      initialCommentsOpen={initialTab === "comments"}
    />
  )
}
